import {FALLBACK_LANGUAGE, i18n} from '@online-offer/i18n';
import {skipToken} from '@reduxjs/toolkit/query';

import {useRef} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useOnMount} from 'shared/web';

import {useGetOnlineOfferFinancialProductsQuery} from '../api/onlineOfferApi';
import {
  GetOnlineOfferFinancialProductsApiArg,
  PublicFinancialProductsResponseBody,
} from '../api/types/onlineOfferTypes';
import {getOfferDirection} from '../utils/getOfferDirection';
import {getWorkspaceFromUri} from '../utils/getWorkspaceFromUri';
import {isOnlineOfferPdfRoute} from '../utils/isOnlineOfferPdfRoute';

const {workspace} = getWorkspaceFromUri();

/** Polling keeps quotes current; focus and reconnect refetches cover paused tabs and offline gaps. */
const FINANCIAL_PRODUCTS_POLLING_INTERVAL_MS = 30_000;

type FinancialProductsContext = {
  financialProducts: PublicFinancialProductsResponseBody | undefined;
  /** Ignore late refresh requests after the page unmounts. */
  refresh: () => void;
  /** Shared read/write address for the current offer. */
  address: GetOnlineOfferFinancialProductsApiArg | null;
  financialProductsFulfilledAt: number | undefined;
  isFinancialProductsFetching: boolean;
  isFinancialProductsLoading: boolean;
  isFinancialProductsErrored: boolean;
  financialProductsError: ReturnType<typeof useGetOnlineOfferFinancialProductsQuery>['error'];
};

/** Buying offers skip this selling-only endpoint; selling quotes own their refresh lifecycle. */
export function useFinancialProducts(): FinancialProductsContext {
  const params = useParams<{offerId: string}>();
  const location = useLocation();
  const isBuying = getOfferDirection(location.pathname) === 'BUYING';
  // PDF rendering waits for one snapshot, so polling would only keep it unsettled.
  const isPrinting = isOnlineOfferPdfRoute(location.pathname);
  const isAddressable = !isBuying && !isNilOrEmpty(params.offerId) && !isNilOrEmpty(workspace);

  const address = isAddressable
    ? {
        offerId: params.offerId ?? '',
        workspace: workspace ?? '',
        language: i18n.resolvedLanguage ?? FALLBACK_LANGUAGE,
      }
    : null;

  const isDisplayed = useRef(true);

  useOnMount(undefined, () => {
    isDisplayed.current = false;
  });

  const {data, isFetching, isLoading, isError, error, refetch, fulfilledTimeStamp} =
    useGetOnlineOfferFinancialProductsQuery(address ?? skipToken, {
      pollingInterval: isPrinting ? undefined : FINANCIAL_PRODUCTS_POLLING_INTERVAL_MS,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });

  return {
    financialProducts: data,
    address,
    financialProductsFulfilledAt: fulfilledTimeStamp,
    isFinancialProductsFetching: isFetching,
    isFinancialProductsLoading: isLoading,
    isFinancialProductsErrored: isError,
    financialProductsError: error,
    refresh: () => {
      if (isDisplayed.current) {
        refetch();
      }
    },
  };
}
